import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Check, Sparkles, Phone, Mail, MapPin, Star, ChevronRight,
  Facebook, Instagram, Linkedin, Users, Navigation, Store, BadgePercent, Gift, Smartphone,
  CalendarCheck, Zap, TrendingUp, Dumbbell,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { useI18n, LanguageSwitcher } from "@/i18n";
import {
  TEAM, PROFILE_CARDS, PILLARS, OFFERS, HERO_IMAGES, CONTACT, COACH_TEAM_IMG, COACH_PORTAL,
  MAP_EMBED, MAP_GMAPS, MAP_WAZE, APP_STORE_URL, PLAY_STORE_URL, CERCLE_URL,
} from "@/lib/siteData";
import { PILLAR_LIST } from "@/lib/pillarContent";
import { BookingDialog } from "@/components/BookingDialog";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.07 } }),
};

const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2 1.6 3.6 3.5 3.9v2.5c-1.3.1-2.6-.3-3.7-1v6.6c0 3.3-2.7 6-6 6s-6-2.7-6-6 2.7-6 6-6c.3 0 .6 0 .9.1v2.7c-.3-.1-.6-.2-.9-.2-1.8 0-3.2 1.5-3.2 3.3s1.4 3.3 3.2 3.3 3.3-1.4 3.3-3.2V3h2.9z" />
  </svg>
);

const WazeIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C6.9 2 2.8 5.8 2.8 10.4c0 2.4 1.1 4.6 2.9 6.1-.1.7-.5 1.7-1.5 2.6-.3.3-.1.8.3.9 2 .3 3.7-.4 4.8-1.1 1 .2 1.9.3 2.9.3 5.1 0 9.2-3.8 9.2-8.4S17.1 2 12 2zm-3.4 9.6c-.7 0-1.2-.6-1.2-1.2s.6-1.2 1.2-1.2 1.2.6 1.2 1.2-.5 1.2-1.2 1.2zm6.8 0c-.7 0-1.2-.6-1.2-1.2s.6-1.2 1.2-1.2 1.2.6 1.2 1.2-.5 1.2-1.2 1.2zm-3.4 4.3c-1.9 0-3.5-1.1-4-2.6-.1-.4.2-.8.6-.8h6.8c.4 0 .7.4.6.8-.5 1.5-2.1 2.6-4 2.6z" />
  </svg>
);

/* "Kinetic Awakening": Pulse → Breath → Reveal → Curtain Up (premium intro). */
function IntroOverlay() {
  const [show, setShow] = useState(true);
  const words = ["Restart", "Your", "Body"];
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="intro-overlay"
          initial={{ y: "0%", borderBottomLeftRadius: "0%", borderBottomRightRadius: "0%" }}
          animate={{ y: "-100%", borderBottomLeftRadius: "42%", borderBottomRightRadius: "42%" }}
          transition={{ delay: 2.15, duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
          onAnimationComplete={() => setShow(false)}
          className="fixed inset-0 z-[100] bg-[#1a2d56] flex flex-col items-center justify-center origin-top overflow-hidden bh-grain"
        >
          {/* Step 1 — The Pulse (heartbeat) */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.7, 1, 1.7, 0], opacity: [0, 1, 1, 1, 0] }}
            transition={{ duration: 1.5, times: [0, 0.18, 0.45, 0.72, 1], ease: "easeInOut" }}
            className="absolute w-3 h-3 rounded-full bg-[#d7de3b]"
            style={{ boxShadow: "0 0 28px 8px rgba(215,222,59,0.55)" }}
          />
          {/* Step 2 — The Breath (line expansion) */}
          <motion.div
            initial={{ width: 12, opacity: 0 }}
            animate={{ width: ["12px", "72vw", "72vw"], opacity: [0, 1, 0] }}
            transition={{ delay: 0.7, duration: 1.0, ease: [0.76, 0, 0.24, 1] }}
            className="absolute h-[2px] bg-[#d7de3b]"
          />
          {/* Step 3 — Logo + baseline reveal */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.4 }}
            className="text-center relative z-10"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.6, ease: "easeOut" }}
            >
              <Logo imgClassName="h-12 sm:h-16 mx-auto mb-6" variant="dark" />
            </motion.div>
            <motion.div
              className="flex items-center justify-center gap-3 sm:gap-4"
              initial="hidden" animate="show"
              variants={{ show: { transition: { delayChildren: 1.15, staggerChildren: 0.14 } } }}
            >
              {words.map((w) => (
                <span key={w} className="overflow-hidden inline-block" style={{ perspective: 700 }}>
                  <motion.span
                    variants={{
                      hidden: { y: "110%", opacity: 0, rotateX: 50 },
                      show: { y: "0%", opacity: 1, rotateX: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
                    }}
                    className={`inline-block font-head text-2xl sm:text-4xl font-light tracking-[0.18em] uppercase ${w === "Body" ? "text-[#d7de3b]" : "text-white"}`}
                  >
                    {w}
                  </motion.span>
                </span>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const goTest = () => navigate("/test");
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);
  const marquee = t("marquee");

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#4b5b7c]">
      <IntroOverlay />
      <Nav navigate={navigate} t={t} goTest={goTest} />
      <Hero t={t} goTest={goTest} openBooking={openBooking} />
      <Marquee marquee={marquee} />
      <Why t={t} />
      <TestSection t={t} goTest={goTest} />
      <Profiles t={t} goTest={goTest} />
      <Journey t={t} />
      <Team t={t} lang={lang} />
      <Testimonials t={t} />
      <Offers t={t} goTest={goTest} openBooking={openBooking} />
      <PartnerOffers t={t} />
      <AppBand t={t} />
      <FindUs t={t} />
      <ContactBand t={t} goTest={goTest} />
      <Footer navigate={navigate} t={t} />
      <BookingDialog open={bookingOpen} onOpenChange={setBookingOpen} source="Landing" />
    </div>
  );
}

/* ---------------------------------------------------------------- Nav */
function Nav({ navigate, t, goTest }) {
  const links = [
    { href: "#why", label: t("nav_why") },
    { href: "#test", label: t("nav_test") },
    { href: "#profils", label: t("nav_profiles") },
    { href: "#equipe", label: t("nav_team") },
    { href: "#offres", label: t("nav_offers") },
    { href: "#partenaires", label: t("nav_partners") },
    { href: "#acces", label: t("nav_access") },
  ];
  return (
    <header className="sticky top-0 z-50 bg-[#fafafa]/85 backdrop-blur-md border-b border-[#e8ebf2]">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
        <Logo imgClassName="h-12 sm:h-14" />
        <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-[#1a2d56]">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-[#4b5b7c] transition-colors">{l.label}</a>
          ))}
          <button data-testid="header-admin-link" onClick={() => navigate("/admin/login")}
            className="text-[#9aa6c0] hover:text-[#1a2d56] transition-colors">{t("nav_team_area")}</button>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="light" />
          <button data-testid="nav-start-button" onClick={goTest}
            className="hidden sm:inline-flex items-center gap-2 bg-[#1a2d56] text-white font-medium rounded-full px-5 py-2.5 text-sm hover:bg-[#111e3a] transition-colors">
            {t("nav_start")} <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- Hero */
function HeroSlideshow() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="absolute inset-0" data-testid="hero-slideshow">
      {HERO_IMAGES.map((src, i) => (
        <motion.img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          initial={false}
          animate={{ opacity: i === idx ? 1 : 0, scale: i === idx ? 1 : 1.04 }}
          transition={{ opacity: { duration: 0.9, ease: "easeInOut" }, scale: { duration: 2.2, ease: "easeOut" } }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a2d56]/95 via-[#1a2d56]/80 to-[#1a2d56]/30" />
    </div>
  );
}

function Hero({ t, goTest, openBooking }) {
  const stats = [
    { n: t("hero_stat1_n"), l: t("hero_stat1_l") },
    { n: t("hero_stat2_n"), l: t("hero_stat2_l") },
    { n: t("hero_stat3_n"), l: t("hero_stat3_l") },
  ];
  return (
    <section className="relative overflow-hidden">
      <HeroSlideshow />
      <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="max-w-2xl">
          <motion.span
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.25, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white text-xs font-semibold uppercase tracking-[0.18em] mb-7 border border-white/15">
            <Sparkles size={14} className="text-[#d7de3b]" /> {t("hero_badge")}
          </motion.span>
          <h1 className="font-head font-light text-white leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl overflow-hidden">
            <motion.span className="block"
              initial={{ opacity: 0, y: "100%" }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.4, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              {t("hero_title_top")}
            </motion.span>
            <motion.span className="block font-semibold text-[#d7de3b]"
              initial={{ opacity: 0, y: "100%" }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.55, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              {t("hero_title_bottom")}
            </motion.span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2.8, duration: 0.6 }}
            className="mt-7 text-lg sm:text-xl text-white/85 leading-relaxed max-w-xl">{t("hero_sub")}</motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.0, duration: 0.6 }}
            className="mt-9 flex flex-col sm:flex-row gap-4">
            <button data-testid="hero-test-button" onClick={goTest}
              className="group inline-flex items-center justify-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl text-sm">
              {t("hero_cta")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button data-testid="hero-book-button" onClick={openBooking}
              className="inline-flex items-center justify-center gap-2 border-2 border-white/40 text-white font-semibold rounded-full px-8 py-4 transition-all duration-300 hover:bg-white hover:text-[#1a2d56] text-sm">
              {t("hero_cta2")}
            </button>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3.2, duration: 0.6 }}
            className="mt-5 text-sm text-white/60">{t("hero_duration")}</motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.35, duration: 0.6 }}
            className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {stats.map((s) => (
              <div key={s.l}>
                <div className="font-head text-3xl sm:text-4xl font-semibold text-[#d7de3b]">{s.n}</div>
                <div className="text-xs sm:text-sm text-white/70 mt-1 leading-snug">{s.l}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Marquee */
function Marquee({ marquee }) {
  return (
    <div className="bg-[#1a2d56] py-4 overflow-hidden">
      <div className="bh-marquee-track">
        {[...marquee, ...marquee].map((m, i) => (
          <span key={`${m}-${i}`} className="inline-flex items-center gap-3 text-white/85 font-head font-medium text-base">
            {m} <span className="text-[#d7de3b]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- Section header */
function Head({ label, title, sub, light }) {
  return (
    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
      className="max-w-3xl">
      {label && <span className={`text-sm uppercase tracking-[0.2em] font-semibold ${light ? "text-[#d7de3b]" : "text-[#9aa6c0]"}`}>{label}</span>}
      <h2 className={`font-head text-3xl sm:text-4xl tracking-tight mt-3 ${light ? "text-white" : "text-[#1a2d56]"}`}>{title}</h2>
      {sub && <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-white/70" : "text-[#4b5b7c]"}`}>{sub}</p>}
    </motion.div>
  );
}

/* ---------------------------------------------------------------- Why */
function Why({ t }) {
  return (
    <section id="why" className="max-w-7xl mx-auto px-6 py-24 md:py-32">
      <Head label={t("nav_why")} title={t("why_title")} sub={t("why_sub")} />
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PILLARS.map((p, i) => (
          <motion.div key={p.key} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="bg-white rounded-2xl p-8 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(26,45,86,0.08)] hover:-translate-y-1 transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-[#1a2d56] flex items-center justify-center text-[#d7de3b]">
              <p.icon size={22} strokeWidth={1.6} />
            </div>
            <h3 className="font-head text-xl font-medium text-[#1a2d56] mt-5">{t(`${p.key}_t`)}</h3>
            <p className="mt-2 leading-relaxed">{t(`${p.key}_d`)}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Test (dominant) */
function TestSection({ t, goTest }) {
  const points = [t("test_point1"), t("test_point2"), t("test_point3")];
  return (
    <section id="test" className="px-6 pb-8">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}
        className="max-w-6xl mx-auto bg-[#1a2d56] rounded-[2rem] px-8 py-14 md:px-16 md:py-20 relative overflow-hidden bh-grain">
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#d7de3b]/10 blur-2xl" />
        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#d7de3b]">{t("test_label")}</span>
            <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mt-4 leading-tight">
              {t("test_title")}
            </h2>
            <p className="mt-5 text-white/75 text-lg leading-relaxed">{t("test_sub")}</p>
            <button data-testid="test-section-cta" onClick={goTest}
              className="mt-9 group inline-flex items-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-semibold rounded-full px-8 py-4 hover:bg-white transition-colors text-sm">
              {t("test_cta")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
          <div className="space-y-4">
            {points.map((p, i) => (
              <motion.div key={p} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
                className="flex items-center gap-4 bg-white/[0.06] border border-white/10 rounded-2xl px-6 py-5">
                <span className="shrink-0 w-9 h-9 rounded-full bg-[#d7de3b] text-[#1a2d56] font-bold flex items-center justify-center">{i + 1}</span>
                <span className="text-white/90">{p}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------------------------------------------------------------- Profiles */
function Profiles({ t, goTest }) {
  return (
    <section id="profils" className="max-w-7xl mx-auto px-6 py-24 md:py-32">
      <div className="text-center flex flex-col items-center">
        <Head title={t("profiles_title")} sub={t("profiles_sub")} />
      </div>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROFILE_CARDS.map((p, i) => (
          <motion.button key={p.key} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            data-testid={`profile-card-${p.key}`} onClick={goTest}
            className="group text-left bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(26,45,86,0.1)] hover:-translate-y-1 transition-all duration-300">
            <div className="relative h-44 overflow-hidden">
              <img src={p.img} alt={t(`prof_${p.key}_name`)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d56]/70 to-transparent" />
              <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-[#d7de3b] flex items-center justify-center text-[#1a2d56]">
                <p.icon size={22} strokeWidth={1.6} />
              </div>
            </div>
            <div className="p-7">
              <h3 className="font-head text-xl font-medium text-[#1a2d56]">{t(`prof_${p.key}_name`)}</h3>
              <p className="text-sm text-[#9aa6c0] mt-1.5">{t(`prof_${p.key}_sym`)}</p>
              <p className="mt-3 font-medium text-[#4b5b7c] flex items-center gap-1.5">
                <Check size={16} className="text-[#d7de3b]" /> {t(`prof_${p.key}_goal`)}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1a2d56] group-hover:gap-2 transition-all">
                {t("profiles_cta")} <ChevronRight size={16} />
              </span>
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Journey */
function Journey({ t }) {
  const steps = [1, 2, 3, 4, 5].map((n) => ({ n, t: t(`journey_s${n}_t`), d: t(`journey_s${n}_d`) }));
  return (
    <section id="parcours" className="bg-white border-y border-[#e8ebf2] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center flex flex-col items-center">
          <Head title={t("journey_title")} sub={t("journey_sub")} />
        </div>
        <div className="mt-16 grid md:grid-cols-5 gap-6 relative">
          {steps.map((s, i) => (
            <motion.div key={s.n} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="relative text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-[#1a2d56] text-[#d7de3b] flex items-center justify-center font-head text-2xl font-semibold relative z-10">
                {s.n}
              </div>
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-[#e8ebf2]" />
              )}
              <h3 className="font-head text-lg font-medium text-[#1a2d56] mt-5">{s.t}</h3>
              <p className="text-sm mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Team */
function Team({ t, lang }) {
  return (
    <section id="equipe" className="max-w-7xl mx-auto px-6 py-24 md:py-32">
      <div className="text-center flex flex-col items-center">
        <Head label={t("nav_team")} title={t("team_title")} sub={t("team_sub")} />
      </div>

      {/* Coaching team — led by Elysée, with the group photo */}
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}
        className="mt-14 grid lg:grid-cols-2 gap-8 items-stretch bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-[0_10px_40px_rgb(0,0,0,0.05)]">
        <div className="relative min-h-[280px]">
          <img src={COACH_TEAM_IMG} alt={t("team_coach_caption")} data-testid="coach-team-photo"
            className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d56]/40 to-transparent lg:hidden" />
        </div>
        <div className="p-8 lg:p-12 flex flex-col justify-center">
          <div className="inline-flex items-center gap-2 self-start px-3.5 py-1.5 rounded-full bg-[#d7de3b]/25 text-[#1a2d56] text-xs font-bold uppercase tracking-wide">
            <Users size={14} /> {t("team_coach_title")}
          </div>
          <h3 className="font-head text-2xl sm:text-3xl font-medium text-[#1a2d56] mt-5">{t("team_coach_caption")}</h3>
          <p className="mt-4 leading-relaxed">{t("team_coach_sub")}</p>
        </div>
      </motion.div>

      {/* Paramedical & care team */}
      <div className="mt-20 text-center flex flex-col items-center">
        <Head title={t("team_care_title")} sub={t("team_care_sub")} />
      </div>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
        {TEAM.map((m, i) => (
          <motion.div key={m.name} custom={i % 4} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            data-testid={`team-${m.name.toLowerCase()}`}
            className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300">
            <div className="aspect-square overflow-hidden bg-[#eef1f6]">
              <img src={m.img} alt={m.name} loading="lazy" className="w-full h-full object-cover" />
            </div>
            <div className="p-5">
              <h3 className="font-head text-lg font-medium text-[#1a2d56]">{m.name}</h3>
              <p className="text-sm text-[#9aa6c0]">{m.role[lang] || m.role.fr}</p>
              <a href={`tel:${m.phone.replace(/\s/g, "")}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#4b5b7c] hover:text-[#1a2d56]">
                <Phone size={13} /> {m.phone}
              </a>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Testimonials */
function Testimonials({ t }) {
  const items = [1, 2, 3].map((n) => ({ q: t(`testi_${n}_q`), a: t(`testi_${n}_a`) }));
  return (
    <section className="bg-[#1a2d56] py-24 md:py-32 bh-grain">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center flex flex-col items-center">
          <Head title={t("testi_title")} sub={t("testi_sub")} light />
        </div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <motion.div key={it.a} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="bg-white/[0.06] border border-white/10 rounded-2xl p-8">
              <div className="flex gap-1 text-[#d7de3b] mb-4">
                {[0, 1, 2, 3, 4].map((s) => <Star key={s} size={16} fill="#d7de3b" strokeWidth={0} />)}
              </div>
              <p className="text-white/90 leading-relaxed">“{it.q}”</p>
              <p className="mt-5 font-semibold text-[#d7de3b]">{it.a}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Offers */
function Offers({ t, goTest, openBooking }) {
  const feats = [t("offer_feat1"), t("offer_feat2"), t("offer_feat3"), t("offer_feat4")];
  return (
    <section id="offres" className="max-w-5xl mx-auto px-6 py-24 md:py-32">
      <div className="text-center flex flex-col items-center">
        <Head label={t("offers_label")} title={t("offers_title")} sub={t("offers_sub")} />
      </div>
      <div className="mt-14 grid sm:grid-cols-2 gap-6">
        {OFFERS.map((o, i) => (
          <motion.div key={o.key} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className={`relative bg-white rounded-2xl p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 ${
              o.featured ? "border-2 border-[#d7de3b] shadow-[0_20px_50px_rgb(26,45,86,0.12)]" : "border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            }`}>
            {o.featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#d7de3b] text-[#1a2d56] text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
                {t("offer_badge")}
              </span>
            )}
            <h3 className="font-head text-xl font-medium text-[#1a2d56]">{t(`${o.key}_name`)}</h3>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-head text-5xl font-semibold text-[#1a2d56]">{o.price}</span>
              <span className="text-sm text-[#9aa6c0]">{t("offer_period")}</span>
            </div>
            <ul className="mt-7 space-y-3">
              {feats.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check size={18} className="text-[#d7de3b] shrink-0 mt-0.5" /> <span>{f}</span>
                </li>
              ))}
            </ul>
            <button data-testid={`offer-cta-${o.key}`} onClick={openBooking}
              className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all text-sm ${
                o.featured ? "bg-[#1a2d56] text-white hover:bg-[#111e3a]" : "border-2 border-[#1a2d56] text-[#1a2d56] hover:bg-[#1a2d56] hover:text-white"
              }`}>
              {t("offers_cta")} <ArrowRight size={16} />
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Partner offers (Local Benefits) */
function PartnerOffers({ t }) {
  const points = [
    { icon: MapPin, title: t("partners_p1_t"), desc: t("partners_p1_d") },
    { icon: BadgePercent, title: t("partners_p2_t"), desc: t("partners_p2_d") },
    { icon: Gift, title: t("partners_p3_t"), desc: t("partners_p3_d") },
  ];
  return (
    <section id="partenaires" className="bg-[#1a2d56] text-white bh-grain">
      <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#d7de3b] text-[#1a2d56] text-sm font-bold">
            <Store size={15} /> {t("partners_label")}
          </span>
          <h2 className="font-head text-3xl sm:text-4xl font-extrabold mt-5 leading-tight">{t("partners_title")}</h2>
          <p className="mt-4 text-white/80 text-lg leading-relaxed">{t("partners_sub")}</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {points.map(({ icon: Icon, title, desc }, i) => (
            <motion.div key={title} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              className="rounded-2xl bg-white/5 border border-white/10 p-6">
              <span className="w-11 h-11 rounded-xl bg-[#d7de3b] text-[#1a2d56] flex items-center justify-center">
                <Icon size={20} />
              </span>
              <h3 className="font-head text-lg font-bold mt-4">{title}</h3>
              <p className="mt-1.5 text-white/70 text-sm leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-12">
          <a href={CERCLE_URL} target="_blank" rel="noopener noreferrer" data-testid="partners-cta-button"
            className="inline-flex items-center gap-2 bg-[#d7de3b] text-[#1a2d56] font-head font-bold rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">
            {t("partners_cta")} <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Technogym mobile app band */
const AppleGlyph = (
  <svg viewBox="0 0 384 512" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);
const PlayGlyph = (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
    <path d="M4 2.5v19a1 1 0 0 0 1.5.87l16-9.5a1 1 0 0 0 0-1.74l-16-9.5A1 1 0 0 0 4 2.5z" />
  </svg>
);

function StoreBadge({ glyph, top, name, testid, href }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
      aria-label={`Télécharger l'application B.health by Technogym sur ${name}`}
      className="inline-flex items-center gap-3 rounded-xl bg-black text-white px-5 py-3 ring-1 ring-white/10 hover:bg-white hover:text-black transition-colors">
      {glyph}
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-wide opacity-70">{top}</span>
        <span className="block text-sm font-semibold -mt-0.5">{name}</span>
      </span>
    </a>
  );
}

function PhoneMockup() {
  return (
    <div className="relative w-[220px] sm:w-[250px]" data-testid="app-phone-mockup">
      <div className="absolute -inset-6 rounded-[3rem] bg-[#d7de3b]/10 blur-2xl" />
      <div className="relative rounded-[2.4rem] bg-black p-3 shadow-2xl ring-1 ring-white/10">
        <div className="relative rounded-[1.9rem] overflow-hidden bg-black aspect-[9/19] flex flex-col items-center justify-center">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-20 h-5 rounded-full bg-[#0a0a0a]" />
          <img src="/technogym-logo.jpg" alt="Application Technogym B.health" loading="lazy" className="w-40 rounded" />
          <p className="mt-6 text-white/55 text-sm font-medium tracking-[0.2em] uppercase">B.health®</p>
        </div>
      </div>
    </div>
  );
}

function AppBand({ t }) {
  const features = [
    { icon: CalendarCheck, label: t("app_feat1") },
    { icon: Zap, label: t("app_feat2") },
    { icon: TrendingUp, label: t("app_feat3") },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 pt-20 md:pt-28">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} variants={fadeUp}
        className="relative overflow-hidden rounded-[2rem] bg-[#1a2d56] bh-grain text-white p-8 sm:p-12 lg:p-16">
        <div className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#d7de3b]/20 blur-3xl" />
        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-sm font-semibold">
              <Smartphone size={15} className="text-[#d7de3b]" /> {t("app_label")}
            </span>
            <h2 className="font-head text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5 leading-[1.05]">{t("app_title")}</h2>
            <p className="mt-4 text-white/75 text-lg leading-relaxed max-w-md">{t("app_sub")}</p>

            <ul className="mt-7 space-y-3" data-testid="app-features">
              {features.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3">
                  <span className="w-9 h-9 rounded-xl bg-[#d7de3b] text-[#1a2d56] flex items-center justify-center shrink-0">
                    <Icon size={17} />
                  </span>
                  <span className="text-white/90 font-medium">{label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex flex-wrap gap-3" data-testid="app-store-badges">
              <StoreBadge testid="app-store-badge" glyph={AppleGlyph} top={t("app_store_top")} name={t("app_store_name")} href={APP_STORE_URL} />
              <StoreBadge testid="google-play-badge" glyph={PlayGlyph} top={t("app_play_top")} name={t("app_play_name")} href={PLAY_STORE_URL} />
            </div>

            <div className="mt-6 flex gap-3 rounded-2xl bg-[#d7de3b]/10 border border-[#d7de3b]/30 p-4" data-testid="app-pt-note">
              <Dumbbell size={20} className="text-[#d7de3b] shrink-0 mt-0.5" />
              <div>
                <p className="font-head font-bold text-[#d7de3b] text-sm">{t("app_pt_title")}</p>
                <p className="mt-1 text-white/70 text-sm leading-relaxed">{t("app_pt_body")}</p>
              </div>
            </div>

            <div className="mt-7 flex items-center gap-3" data-testid="app-powered-by">
              <span className="text-xs uppercase tracking-wider text-white/50">{t("app_powered")}</span>
              <span className="inline-flex items-center justify-center rounded-lg bg-black w-10 h-10 ring-1 ring-white/10 overflow-hidden">
                <img src="/technogym-logo.jpg" alt="Logo officiel Technogym" loading="lazy" className="w-full h-full object-cover scale-150" />
              </span>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <PhoneMockup />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------------------------------------------------------------- Find us / Access */
function FindUs({ t }) {
  // GDPR: Google Maps sets cookies, so only load it after cookie consent
  // (or an explicit click on the placeholder — an explicit user action).
  const [mapAllowed, setMapAllowed] = useState(
    typeof window !== "undefined" && localStorage.getItem("bh_consent") === "granted"
  );
  useEffect(() => {
    const onGrant = () => setMapAllowed(true);
    window.addEventListener("bh-consent-granted", onGrant);
    return () => window.removeEventListener("bh-consent-granted", onGrant);
  }, []);
  return (
    <section id="acces" className="max-w-7xl mx-auto px-6 py-24 md:py-32">
      <div className="text-center flex flex-col items-center">
        <Head label={t("find_label")} title={t("find_title")} sub={t("find_sub")} />
      </div>
      <div className="mt-14 grid lg:grid-cols-2 gap-8 items-stretch">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}
          className="rounded-3xl overflow-hidden border border-gray-100 shadow-[0_10px_40px_rgb(0,0,0,0.05)] min-h-[340px]">
          {mapAllowed ? (
            <iframe title="B.health — Avenue de la Basilique 14" src={MAP_EMBED} data-testid="map-embed"
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full min-h-[340px] border-0" allowFullScreen />
          ) : (
            <button type="button" data-testid="map-consent-placeholder" onClick={() => setMapAllowed(true)}
              className="w-full h-full min-h-[340px] flex flex-col items-center justify-center gap-4 bg-[#f3f5fb] text-center px-6 hover:bg-[#eaeef8] transition-colors">
              <span className="w-14 h-14 rounded-2xl bg-[#1a2d56] flex items-center justify-center text-[#d7de3b]">
                <MapPin size={24} />
              </span>
              <p className="max-w-sm text-sm text-[#4b5b7c] leading-relaxed">{t("map_consent")}</p>
              <span className="inline-flex items-center gap-2 bg-[#1a2d56] text-white font-semibold rounded-full px-6 py-3 text-sm">
                {t("map_consent_btn")}
              </span>
            </button>
          )}
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={fadeUp}
          className="bg-white rounded-3xl p-8 lg:p-10 border border-gray-100 shadow-[0_10px_40px_rgb(0,0,0,0.05)] flex flex-col justify-center">
          <div className="flex items-start gap-3">
            <span className="w-11 h-11 rounded-xl bg-[#1a2d56] flex items-center justify-center text-[#d7de3b] shrink-0">
              <MapPin size={20} />
            </span>
            <div>
              <p className="font-head text-xl font-medium text-[#1a2d56]">B.health®</p>
              <p className="mt-1 leading-relaxed">{CONTACT.address}</p>
            </div>
          </div>
          <p className="mt-7 text-sm">{t("find_directions")}</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <a data-testid="directions-gmaps" href={MAP_GMAPS} target="_blank" rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-[#1a2d56] text-white font-semibold rounded-full px-6 py-3.5 hover:bg-[#111e3a] transition-colors text-sm">
              <Navigation size={17} className="text-[#d7de3b]" /> {t("find_gmaps")}
            </a>
            <a data-testid="directions-waze" href={MAP_WAZE} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#1a2d56] text-[#1a2d56] font-semibold rounded-full px-6 py-3.5 hover:bg-[#1a2d56] hover:text-white transition-colors text-sm">
              <WazeIcon /> {t("find_waze")}
            </a>
          </div>
          <a data-testid="access-phone" href={`tel:${CONTACT.phoneRaw}`}
            className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#4b5b7c] hover:text-[#1a2d56] transition-colors">
            <Phone size={15} className="text-[#d7de3b]" /> {CONTACT.phone}
          </a>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- Contact band */
function ContactBand({ t, goTest }) {
  return (
    <section className="px-6 pb-24">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} variants={fadeUp}
        className="max-w-6xl mx-auto bg-[#d7de3b] rounded-[2rem] px-8 py-14 md:px-16 text-center">
        <h2 className="font-head text-3xl sm:text-4xl font-medium text-[#1a2d56] tracking-tight">{t("contact_title")}</h2>
        <p className="mt-4 text-[#1a2d56]/80 text-lg max-w-2xl mx-auto">{t("contact_sub")}</p>
        <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
          <button data-testid="band-book-button" onClick={goTest}
            className="inline-flex items-center justify-center gap-2 bg-[#1a2d56] text-white font-semibold rounded-full px-8 py-4 hover:bg-[#111e3a] transition-colors text-sm">
            {t("contact_test")} <ArrowRight size={18} />
          </button>
          <a data-testid="band-phone-button" href={`tel:${CONTACT.phoneRaw}`}
            className="inline-flex items-center justify-center gap-2 bg-white text-[#1a2d56] font-semibold rounded-full px-8 py-4 hover:shadow-lg transition-all text-sm">
            <Phone size={18} /> {CONTACT.phone}
          </a>
        </div>
      </motion.div>
    </section>
  );
}

/* ---------------------------------------------------------------- Footer */
function Footer({ navigate, t }) {
  return (
    <footer className="bg-[#1a2d56] text-white/80 pt-16 pb-10">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-10">
        <div>
          <Logo imgClassName="h-12" variant="dark" />
          <p className="mt-4 text-sm text-white/60 max-w-xs">{t("footer_text")}</p>
        </div>
        <div>
          <h4 className="font-head text-white font-medium mb-4">{t("footer_contact")}</h4>
          <ul className="space-y-2.5 text-sm">
            <li className="flex items-start gap-2.5"><MapPin size={16} className="text-[#d7de3b] shrink-0 mt-0.5" /> {CONTACT.address}</li>
            <li><a href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2.5 hover:text-white"><Phone size={16} className="text-[#d7de3b]" /> {CONTACT.phone}</a></li>
            <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 hover:text-white"><Mail size={16} className="text-[#d7de3b]" /> {CONTACT.email}</a></li>
          </ul>
        </div>
        <div>
          <h4 className="font-head text-white font-medium mb-4">{t("footer_guides")}</h4>
          <ul className="space-y-2.5 text-sm">
            {PILLAR_LIST.map((g) => (
              <li key={g.slug}>
                <button data-testid={`footer-guide-${g.slug}`} onClick={() => navigate(`/${g.slug}`)}
                  className="text-left hover:text-white transition-colors">{g.h1}</button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-head text-white font-medium mb-4">{t("footer_follow")}</h4>
          <div className="flex gap-3">
            {[
              { Icon: Facebook, href: CONTACT.social.facebook },
              { Icon: Instagram, href: CONTACT.social.instagram },
              { Icon: TikTokIcon, href: CONTACT.social.tiktok },
              { Icon: Linkedin, href: CONTACT.social.linkedin },
            ].map(({ Icon, href }) => (
              <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#d7de3b] hover:text-[#1a2d56] flex items-center justify-center transition-colors">
                <Icon size={18} />
              </a>
            ))}
          </div>
          <a data-testid="footer-coach-access" href={COACH_PORTAL} target="_blank" rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d7de3b] hover:text-[#1a2d56] hover:border-[#d7de3b] transition-colors">
            <Users size={16} /> {t("coach_access")}
          </a>
          <p className="mt-2 text-xs text-white/40">{t("coach_access_desc")}</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/50">
        <span>© {new Date().getFullYear()} B.health® · HFM SRL</span>
        <div className="flex gap-5">
          <button data-testid="footer-legal-link" onClick={() => navigate("/mentions-legales")} className="hover:text-white">{t("legal_notice")}</button>
          <button data-testid="footer-privacy-link" onClick={() => navigate("/confidentialite")} className="hover:text-white">{t("privacy_policy")}</button>
        </div>
      </div>
    </footer>
  );
}
